// src/pages/Home.jsx
import {
    Container,
    Typography,
    Box,
    Paper,
    Button,
    Grid,
    keyframes,
} from "@mui/material";

import DownhillSkiingIcon from "@mui/icons-material/DownhillSkiing";
import DirectionsRunIcon from "@mui/icons-material/DirectionsRun";
import GroupsIcon from "@mui/icons-material/Groups";

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

export default function Home() {
    return (
        <Container maxWidth="lg" sx={{ mt: 6, mb: 10 }}>

            {/* HERO SECTION */}
            <Box
                sx={{
                    position: "relative",
                    height: { xs: "auto", md: 320 },
                    py: { xs: 6, md: 0 },
                    borderRadius: 3,
                    overflow: "hidden",
                    mb: 10,
                    animation: `${fadeInUp} 0.6s ease-out`,
                }}
            >
                <Box
                    component="img"
                    src="/ski-photo.jpg"
                    alt="Ski racing"
                    sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        filter: "brightness(0.7)",
                        position: "absolute",
                        inset: 0,
                        zIndex: -1,
                    }}
                />

                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        background:
                            "linear-gradient(135deg, rgba(15,42,74,0.7), rgba(26,77,143,0.4))",
                        zIndex: -1,
                    }}
                />

                <Box
                    sx={{
                        textAlign: "center",
                        color: "white",
                        px: 2,
                    }}
                >
                    <Typography
                        variant="h3"
                        sx={{
                            fontWeight: 700,
                            mb: 1,
                            textShadow: "0 3px 10px rgba(0,0,0,0.4)",
                        }}
                    >
                        Votre partenaire pour le chronométrage sportif
                    </Typography>

                    <Typography
                        variant="h5"
                        sx={{
                            fontWeight: 400,
                            opacity: 0.9,
                            textShadow: "0 2px 6px rgba(0,0,0,0.4)",
                        }}
                    >
                        Précision. Fiabilité. Passion.
                    </Typography>
                </Box>
            </Box>

            {/* SECTION — Pourquoi nous ? */}
            <Box sx={{ mb: 10, textAlign: "center", animation: `${fadeInUp} 0.8s ease-out` }}>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, color: "secondary.light" }}>
                    Nos atouts
                </Typography>

                <Grid
                    container
                    direction="column"
                    spacing={4}
                    alignItems="center"
                    sx={{
                        width: "100%",
                        maxWidth: 700,
                        margin: "0 auto",
                        px: 2,
                    }}
                >
                    {[
                        {
                            title: "Expérience",
                            text: "Plus de 15 ans de chronométrage en compétitions nationales et internationales.",
                            icon: <DownhillSkiingIcon sx={{ color: "white" }} />, // tu peux changer l’icône
                        },
                        {
                            title: "Fiabilité",
                            text: "Technologies modernes et adaptées aux besoins de nos clients.",
                            icon: <DirectionsRunIcon sx={{ color: "white" }} />, // icône placeholder
                        },
                        {
                            title: "Flexibilité",
                            text: "Solutions adaptées aux clubs, écoles, entreprises et grands événements.",
                            icon: <GroupsIcon sx={{ color: "white" }} />, // icône placeholder
                        },
                    ].map((item, index) => (
                        <Grid item key={index} xs={12} sx={{ width: "100%" }}>

                            <Paper
                                elevation={0}
                                sx={{
                                    p: 4,
                                    mb: 4,
                                    animation: `${fadeInUp} 0.8s ease-out`,
                                }}
                            >
                                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                                    <Box
                                        sx={{
                                            width: 40,
                                            height: 40,
                                            borderRadius: "50%",
                                            backgroundColor: "secondary.light",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            mr: 2,
                                        }}
                                    >



                                    {item.icon}
                                </Box>
                                </Box>
                                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                                    {item.title}
                                </Typography>

                                <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
                                    {item.text}
                                </Typography>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* SECTION — Prestations (teasers) */}
            <Box sx={{ mb: 8, animation: `${fadeInUp} 0.8s ease-out` }}>
                <Typography
                    variant="h4"
                    sx={{
                        textAlign: "center",
                        fontWeight: 700,
                        mb: 3,
                        color: "primary.main",
                    }}
                >
                    Nos prestations
                </Typography>

                <Typography
                    variant="body1"
                    sx={{
                        textAlign: "center",
                        maxWidth: 700,
                        margin: "0 auto",
                        mb: 4,
                        color: "text.secondary",
                    }}
                >
                    Découvrez nos solutions de chronométrage adaptées à tous les sports.
                </Typography>

                <Grid
                    container
                    direction="column"
                    spacing={4}
                    alignItems="center"
                    sx={{
                        width: "100%",
                        maxWidth: 700,
                        margin: "0 auto",
                        px: 2,
                    }}
                >
                    {[
                        {
                            title: "Ski & Telemark",
                            text: "Chronométrage pour tous niveaux.",
                            icon: <DownhillSkiingIcon sx={{ color: "white" }} />,
                        },
                        {
                            title: "Course à pied & vélo",
                            text: "Technologie Ubidium RaceResult.",
                            icon: <DirectionsRunIcon sx={{ color: "white" }} />,
                        },
                        {
                            title: "Concours par équipes",
                            text: "Formats personnalisés et classements cumulés.",
                            icon: <GroupsIcon sx={{ color: "white" }} />,
                        },
                    ].map((service, index) => (
                        <Grid item key={index} xs={12} sx={{ width: "100%" }}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 4,
                                    mb: 4,
                                    animation: `${fadeInUp} 0.8s ease-out`,
                                }}
                            >
                                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                                    <Box
                                        sx={{
                                            width: 40,
                                            height: 40,
                                            borderRadius: "50%",
                                            backgroundColor: "primary.light",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            mr: 2,
                                        }}
                                    >
                                    {service.icon}
                                </Box>
                                </Box>
                                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                                    {service.title}
                                </Typography>

                                <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
                                    {service.text}
                                </Typography>

                                <Button size="small" href="/prestations" color="primary">
                                    En savoir plus
                                </Button>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>

                </Box>

            {/* SECTION — Contact (version card premium) */}
            <Grid
                container
                direction="column"
                alignItems="center"
                sx={{
                    width: "100%",
                    maxWidth: 700,
                    margin: "0 auto",
                    px: 2,
                    mt: 10,
                }}
            >
                <Grid item xs={12} sx={{ width: "100%" }}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 4,
                            mb: 4,
                            animation: `${fadeInUp} 0.8s ease-out`,
                        }}
                    >
                        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                            <Box
                                sx={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: "50%",
                                    backgroundColor: "primary.light",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    mr: 2,
                                }}
                            >
                            <GroupsIcon sx={{ color: "white" }} />
                        </Box>
                        </Box>
                        <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
                             Vous êtes à la recherche d'un partenaire chronométrage?
                        </Typography>

                        <Typography variant="body1" sx={{ mb: 3, color: "text.secondary" }}>
                            Contactez‑nous pour une offre personnalisée ou pour toute question.
                        </Typography>

                        <Button
                            variant="contained"
                            color="secondary"
                            size="large"
                            href="mailto:info@321-timing.ch"
                        >
                            Nous contacter
                        </Button>
                    </Paper>
                </Grid>
            </Grid>
        </Container>
    );
}
